import type { Effect } from '@rxsv/core';
import { fromActions } from '@rxsv/core';
import { merge, of, throwError } from 'rxjs';
import {
    map,
    filter,
    withLatestFrom,
    switchMap,
    mergeMap,
    catchError,
    tap,
    ignoreElements,
    scan,
} from 'rxjs/operators';

import type { RootService } from 'portlet/config/service';
import {
    Specification as SpecificationModel,
    View as ViewModel,
    Config,
    SettingDefinition,
    initEmailData,
    Settings,
    FilterDataArray,
} from 'portlet/models';
import { SettingStack, AutoDateService } from 'portlet/services';
import { concatUniqByProp, isNotNullable } from 'portlet/common/utils';
import { appError, isAppError } from 'portlet/common/errors';
import type { ActionTransition } from 'header/types';

import { AppAction, AppErrorActions } from './actions';
import type { AppState } from './reducer';
import * as FiltersSettings from './filters-settings';
import * as View from './view';
import * as Auth from './auth';
import * as Init from './init';
import * as EmailSupport from './email-support';
import * as Performance from './performance';

export type AppEffect = Effect<AppAction, AppState>;
export type EffectFactory<T = RootService> = (service: T) => AppEffect;

const mergeSignals = concatUniqByProp('name');

const initEffect: EffectFactory =
    ({ specService, autoDateService, mailingService }) =>
    (action$, state$) =>
        action$.pipe(
            fromActions(AppAction.portlet.PORTLET_INIT),
            map(({ payload }) => payload),
            switchMap(({ config, additionalAttrs, spec }) => {
                const spec$ = spec
                    ? specService.validateAndResolveSpec(spec)
                    : config.src
                      ? specService.getSpecificationFromUrl(config.src)
                      : config.pid
                        ? specService.getSpecification(config.pid)
                        : throwError(() => appError('You need to provide `pid`, `src` or `spec`'));

                return spec$.pipe(
                    mergeMap(({ metadata, spec }) => {
                        if (spec.supportContentArea && spec.supportContentArea)
                            return mailingService.getContentAreaMail(spec.supportContentArea).pipe(
                                map(email => ({
                                    metadata,
                                    spec,
                                    email,
                                })),
                                catchError(error => {
                                    console.error(error);
                                    return of({ metadata, spec, email: initEmailData.recipient });
                                }), // we are catching error here to prevent portlet from crashing because of problem with content
                            );
                        return of({ metadata, spec, email: initEmailData.recipient });
                    }),
                    withLatestFrom(state$),
                    mergeMap(([{ metadata, spec, email }, state]) => {
                        const resolvedSpec = specService.resolveAdditionalAttributes(
                            spec,
                            additionalAttrs,
                        );
                        const currentViewId =
                            FiltersSettings.Selectors.settingSelector(state).VIEWID ||
                            config.viewid ||
                            SpecificationModel.getDefaultViewId(resolvedSpec);
                        const currentView = SpecificationModel.getViewById(
                            resolvedSpec,
                            currentViewId,
                        );
                        if (!currentView) throw appError(`View of id: ${currentViewId} not found`);

                        const filters = ViewModel.getDefaultFilters(currentView);
                        const observedFilters =
                            SpecificationModel.getObservedFiltersPerView(resolvedSpec);

                        const renderView = View.Action.RENDER_VIEW(currentView);

                        const [definitions, viewSettingDictionary] =
                            SettingDefinition.toDefinitionMapWithViewDictionary(
                                resolvedSpec,
                                SpecificationModel.getRenderedViews(resolvedSpec),
                                currentViewId,
                            );

                        // merging with current settings/filters in case `SET_SETTING`/`SET_FILTER`
                        // was called before initialization was complete
                        //
                        // we do the same for extra signal overwrites
                        const currentFilters = FiltersSettings.Selectors.allFilterSelector(state);
                        const currentSettingStack =
                            FiltersSettings.Selectors.settingStackSelector(state);
                        const currentSignals = FiltersSettings.Selectors.signalsSelector(state);
                        const signals = mergeSignals(config.signals, currentSignals);

                        const settingStack: SettingStack = SettingStack.merge(currentSettingStack, {
                            defaultSettings: SettingDefinition.toValueMap(
                                resolvedSpec,
                                null,
                                resolvedSpec.settings,
                            ),
                            computedDefaultSettings: SettingDefinition.toValueMap(
                                resolvedSpec,
                                currentView,
                                currentView.settings || {},
                            ),
                            settings: Config.toSettings(config),
                        });

                        const settings = Settings.merge(
                            settingStack.defaultSettings,
                            settingStack.computedDefaultSettings,
                            settingStack.settings,
                        );

                        const initFilters = AppAction.filters.INIT_FILTERS_AND_SETTINGS({
                            ...settingStack,
                            filters: FilterDataArray.merge(filters, currentFilters),
                            observedFilters,
                            definitions,
                            viewSettingDictionary,
                            specification: resolvedSpec,
                            signals,
                        });

                        const initEmail = AppAction.email.SET_EMAIL_DATA({
                            ...initEmailData,
                            recipient: email,
                            subject: metadata?.id
                                ? `Support request for ${metadata.id}`
                                : initEmailData.subject,
                        });

                        const init = AppAction.portlet.PORTLET_INIT_SUCCESS({
                            config,
                            metadata,
                            spec: resolvedSpec,
                        });

                        const autoDateFilter =
                            !config.suppressautodate &&
                            AutoDateService.evaluateAutoDate(
                                spec,
                                currentView,
                                settings,
                                autoDateService.now(),
                            );

                        const autoDateAction = autoDateFilter
                            ? AppAction.filters.CHANGE_FILTER_VALUE(autoDateFilter)
                            : null;

                        return [init, initEmail, initFilters, autoDateAction, renderView].filter(
                            isNotNullable,
                        );
                    }),
                    catchError(err => of(AppAction.portlet.PORTLET_INIT_ERROR(err))),
                );
            }),
        );

const triggerRenderEffect: EffectFactory = () => (action$, state$) => {
    const renderTrigger$ = merge(
        action$.pipe(fromActions(FiltersSettings.Action.SET_FILTER_VALUE)),
        action$.pipe(fromActions(FiltersSettings.Action.CHANGE_SETTING_VALUE)),
        action$.pipe(fromActions(FiltersSettings.Action.CLEAR_FILTER)),
        action$.pipe(fromActions(FiltersSettings.Action.CLEAR_SETTING_VALUES)),
        action$.pipe(fromActions(FiltersSettings.Action.DEFINITIONS_LOADED)),
    );

    const changeView = renderTrigger$.pipe(
        withLatestFrom(state$),
        map(([_, state]) => {
            const viewId = FiltersSettings.Selectors.currentViewId(state);
            if (!viewId) return null;

            const spec = Init.Selectors.specificationSelector(state);
            if (!spec) return null;

            const view = SpecificationModel.getViewById(spec, viewId);
            if (!view) return null;

            return View.Action.RENDER_VIEW(view);
        }),
        filter(isNotNullable),
    );

    return changeView;
};

const errorLogger: EffectFactory =
    ({ logger, errorMonitoringService }) =>
    action$ =>
        action$.pipe(
            fromActions(...AppErrorActions),
            filter(action => !(isAppError(action.payload) && action.payload.severity === 'WARN')),
            tap(action => errorMonitoringService.captureErrorAction(action)),
            map(({ payload }) => payload),
            tap(logger.error),
            ignoreElements(),
        );

const telemetryEffect: EffectFactory =
    ({ telemetryService }) =>
    (action$, state$) =>
        action$.pipe(
            fromActions(
                AppAction.portlet.PORTLET_INIT_SUCCESS,
                AppAction.portlet.CHART_RENDERED,
                AppAction.portlet.CHART_RENDER_ERROR,
            ),
            withLatestFrom(state$),
            tap(([action, state]) => {
                const user = Auth.Selectors.userSelector(state);
                const config = Init.Selectors.configSelector(state);
                const spec = Init.Selectors.specificationSelector(state);
                const allowTelemetry =
                    config?.telemetry && (spec?.allowTelemetry ?? config?.env === 'prod');
                const componentId =
                    (config?.telemetry && config.telemetry?.ComponentID) || 'unknown';
                if (!user || !config || !allowTelemetry) return;
                let SubcomponentID;
                switch (true) {
                    case !!(config.pid && config.uniqueid):
                        SubcomponentID = `${config.pid}||${config.uniqueid}`;
                        break;
                    case !!(config.pid && !config.uniqueid):
                        SubcomponentID = config.pid;
                        break;
                    case !!(config.uniqueid && !config.pid):
                        SubcomponentID = config.uniqueid;
                        break;
                    case !!(config.telemetry && config.telemetry.SubcomponentID):
                        SubcomponentID = config.telemetry.SubcomponentID;
                        break;
                    default:
                        SubcomponentID = null;
                        break;
                }

                let payload = {
                    ...config.telemetry,
                    UserID: user.cwid || user.id || '',
                    PlatformID: `vizit_${config.env}`,
                    SubcomponentID,
                    ComponentID: componentId,
                };
                switch (action.type) {
                    case 'CHART_RENDERED':
                        payload = { ...payload, EventTypeID: 'portlet_data_loaded' };
                        break;
                    case 'CHART_RENDER_ERROR':
                        payload = { ...payload, EventTypeID: 'portlet_data_load_failed' };
                        break;
                    default:
                        // prevent from sending telemetry records for other action types, without EventTypeID
                        return;
                }
                return telemetryService
                    .createRecord(payload)
                    .catch(err => console.warn('Failure in telemetry event', err));
            }),
            ignoreElements(),
        );

const trackFiltersAppliance: EffectFactory = () => (action$, state$) =>
    merge(
        action$.pipe(
            fromActions(FiltersSettings.Action.SET_FILTER_VALUE),
            withLatestFrom(state$),
            map(([_action, state]) => {
                const viewId = FiltersSettings.Selectors.currentViewId(state);
                if (!viewId) return null;

                const spec = Init.Selectors.specificationSelector(state);
                if (!spec) return null;

                const view = SpecificationModel.getViewById(spec, viewId);
                if (!view) return null;

                Performance.Action.SAVE_FILTERS_DELAY_DELTA_TIME(0);
                return Performance.Action.SAVE_FILTERS_DELAY_TIMESTAMP(Date.now());
            }),
            filter(isNotNullable),
        ),
        action$.pipe(
            fromActions(FiltersSettings.Action.APPLIED_FILTER_VALUES),
            withLatestFrom(state$),
            map(([_action, state]) => {
                const diff = Date.now() - state.performance.filtersDelayTimestamp;
                Performance.Action.SAVE_FILTERS_DELAY_TIMESTAMP(0);
                return Performance.Action.SAVE_FILTERS_DELAY_DELTA_TIME(diff);
            }),
        ),
    );

const trackPerformanceEffect: EffectFactory = () => (action$, state$) =>
    merge(
        action$.pipe(
            fromActions(AppAction.portlet.PORTLET_RESET),
            map(() => {
                return Performance.Action.RESET_PERFORMANCE_TRACES();
            }),
        ),
        action$.pipe(
            fromActions(
                AppAction.portlet.PORTLET_INIT,
                AppAction.portlet.PORTLET_INIT_SUCCESS,
                AppAction.view.RENDER_VIEW,
                AppAction.view.VIEW_DATA_LOADED,
                AppAction.portlet.CHART_RENDERED,
                AppAction.view.RELOAD_DATA,
            ),
            scan(
                (
                    state: Performance.PerformanceState,
                    action: AppAction & { type: Performance.ActionType },
                ) => {
                    const now = Date.now();
                    if (!state?.waitingForAction.includes(action.type)) return state;
                    return {
                        currentAction: action.type,
                        currentActionTime: now,
                        waitingForAction: Performance.initialState.actionsTransitions[action.type],
                        latestTrace: {
                            from: state.currentAction,
                            to: action.type,
                            deltaTime: now - state.currentActionTime,
                            description:
                                state.currentAction === null
                                    ? 'start of initialization'
                                    : Performance.descriptions[state?.currentAction],
                        },
                    };
                },
                {
                    currentAction: null,
                    currentActionTime: Date.now(),
                    waitingForAction: [AppAction.portlet.PORTLET_INIT.type],
                    latestTrace: null,
                },
            ),
            filter(isNotNullable),
            withLatestFrom(state$),
            map(([e, state]) => {
                const config = Init.Selectors.configSelector(state);
                const spec = Init.Selectors.specificationSelector(state);
                const trace = e.latestTrace;
                if (!trace) return null;
                return Performance.Action.SAVE_PERFORMANCE_TRACE({
                    transition: trace as ActionTransition,
                    threshold: spec?.performanceThreshold || config?.performancethreshold,
                });
            }),
            filter(isNotNullable),
        ),
    );

export const effectFactories: EffectFactory[] = [
    initEffect,
    triggerRenderEffect,
    View.effect,
    FiltersSettings.effect,
    errorLogger,
    EmailSupport.effect,
    Auth.effect,
    telemetryEffect,
    trackPerformanceEffect,
    trackFiltersAppliance,
].filter(isNotNullable);
